import React from "react";

export type CheckrPackage = {
  _id: string;
  planTitle?: string;
  planDescription?: string[] | string;
  price: number;
  currency?: string;
  currencySymbol?: string;
};

type CheckrPackageSelectionDialogProps = {
  packages: CheckrPackage[];
  onClose: () => void;
  onSelectPackage: (checkrPackage: CheckrPackage) => void;
};

export const CheckrPackageSelectionDialog = ({
  packages,
  onClose,
  onSelectPackage,
}: CheckrPackageSelectionDialogProps) => {
  const formatPrice = (checkrPackage: CheckrPackage) =>
    `${checkrPackage.currencySymbol || "$"}${Number(
      checkrPackage.price,
    ).toFixed(2)}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl sm:p-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <p className="mb-1 text-sm font-semibold uppercase tracking-wide text-[#ff39b3]">
              Background check
            </p>
            <h3 className="text-xl font-bold text-gray-900 sm:text-2xl">
              Select a Checkr package
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close package selection"
            className="rounded-full p-2 text-2xl leading-none text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
          >
            &times;
          </button>
        </div>
        {packages.length === 0 ? (
          <p className="rounded-lg bg-gray-50 p-6 text-center text-gray-600">
            No active Checkr packages are currently available.
          </p>
        ) : (
          <div className="grid grid-cols-1 items-stretch gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {packages.map((checkrPackage) => {
              const descriptions = Array.isArray(
                checkrPackage.planDescription,
              )
                ? checkrPackage.planDescription
                : checkrPackage.planDescription
                  ? [checkrPackage.planDescription]
                  : [];
              return (
                <article
                  key={checkrPackage._id}
                  className="flex min-w-0 flex-col overflow-hidden rounded-2xl border border-pink-100 bg-gradient-to-b from-pink-50/70 to-white shadow-sm transition duration-200 hover:-translate-y-1 hover:border-[#ff39b3]/50 hover:shadow-lg"
                >
                  <div className="flex flex-1 flex-col p-5 sm:p-6">
                    <div className="mb-4 flex items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-pink-100 text-[#ff39b3]">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          className="h-5 w-5"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                          />
                        </svg>
                      </span>
                      <h4 className="pt-1 text-lg font-bold leading-snug text-gray-900 sm:text-xl">
                        {checkrPackage.planTitle || "Checkr package"}
                      </h4>
                    </div>
                    <ul className="mb-6 flex-1 space-y-3 text-sm leading-5 text-gray-700">
                      {descriptions.map((feature, index) => (
                        <li
                          key={`${checkrPackage._id}-feature-${index}`}
                          className="flex items-start gap-2.5"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="mt-0.5 h-5 w-5 shrink-0 text-[#ff39b3]"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.78-9.72a.75.75 0 0 0-1.06-1.06l-3.47 3.47-1.47-1.47a.75.75 0 0 0-1.06 1.06l2 2a.75.75 0 0 0 1.06 0l4-4Z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mb-5 border-t border-pink-100 pt-4">
                      <p className="text-3xl font-bold tracking-tight text-gray-900">
                        {formatPrice(checkrPackage)}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        One-time background check
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onSelectPackage(checkrPackage)}
                      className="w-full rounded-lg bg-[#ff39b3] px-4 py-3 font-bold text-white shadow-sm transition hover:bg-[#e7339f] focus:outline-none focus:ring-2 focus:ring-[#ff39b3] focus:ring-offset-2"
                    >
                      Select plan
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
